import React from "react";
import PropTypes from "prop-types";
import { Badge, Card, CardTitle, Col, Row, Table } from "reactstrap";
import NumberFormat from "react-number-format";
import classNames from "classnames";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ItemImageTitleSubProperty from "../common/ItemImageTitleSubProperty";
import Logo from "../navbar/Logo";
import PlanillaBalanceTiles from "./PlanillaBalanceTiles";
import { roundAmount } from "../../helpers/currencyConversion";

/**
 * HO-TASK-247: la planilla de liquidación de un propietario dentro del reporte
 * del administrador.
 *
 * Los datos llegan tal cual los arma PlanillaNoticeData en el backend -- el
 * mismo servicio que usan el correo al propietario y el PDF adjunto -- así que
 * lo que ve el administrador es lo que recibió el propietario y no una
 * estimación calculada en el navegador.
 *
 * El diseño es el de la aplicación, no el del PDF: se arma con los mismos
 * componentes y las mismas clases que la pantalla de un efecto por cobrar
 * (EditDebtReceivable), que ya muestra esta misma planilla.
 */

const formatDate = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : "No disponible";

const orDash = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

const PropertyPlanillaReport = ({
  planilla,
  baseCurrencySymbol = "",
  quoteCurrencyIso = "",
  rateValue = 0,
  rateDateLabel = "",
  appliedRateDateLabel = "",
  showItemsInQuote = false,
  showTotalsInQuote = true,
  logo = "",
  getRateForRow,
}) => {
  const { condominium, owner, dates, amounts, banks, tables, totals } = planilla;
  const hasConversion = Boolean(quoteCurrencyIso) && Number(rateValue) > 0;

  /**
   * El importe en la moneda base y, debajo, su conversión.
   *
   * HO-TASK-246: dentro de las tablas la conversión depende del interruptor
   * del administrador, porque un monto convertido bajo cada gasto llena la
   * planilla de números que muchos no quieren ver. Los totales la llevan
   * siempre: es lo que el propietario tiene que pagar.
   */
  const Amount = ({ value, valueBs, className, isTotal, customRate }) => {
    const rateToUse = customRate || rateValue;
    const amount = Number(roundAmount(value) || 0);
    const amountBs =
      valueBs !== undefined && valueBs !== null ? Number(valueBs) : amount * rateToUse;
    return (
      <>
        <div className={className}>
          <NumberFormat
            displayType="text"
            prefix={`${baseCurrencySymbol} `}
            allowedDecimalSeparators={true}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator={true}
            value={amount}
          />
        </div>
        {hasConversion && (isTotal ? showTotalsInQuote : showItemsInQuote) && (
          <div className="fs--1 text-600">
            <NumberFormat
              displayType="text"
              prefix={`${quoteCurrencyIso} `}
              allowedDecimalSeparators={true}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator={true}
              value={amountBs}
            />
          </div>
        )}
      </>
    );
  };

  /**
   * Gastos ordinarios y extraordinarios: las dos columnas de importe del PDF
   * -- Monto es el gasto completo del condominio y Cuota lo que le toca a esta
   * propiedad.
   */
  const ExpenseTable = ({
    title,
    rows,
    totalLabel,
    total,
    totalBs,
    shareKey = "share",
    shareLabel = "Cuota",
    dateLabel = "Fecha del gasto",
    credit,
  }) => {
    if (!rows || rows.length === 0) return null;

    const sign = credit ? -1 : 1;
    const signed = (value) =>
      value !== undefined && value !== null ? (Number(value) || 0) * sign : undefined;

    console.log("tables: ", tables);

    return (
      <div className="table-responsive mb-3 fs--1">
        <Table striped className="border-bottom">
          <thead>
            <tr>
              <th className="border-0">{title}</th>
              <th className="border-0">Descripción</th>
              <th className="border-0" style={{whiteSpace: "nowrap"}}>{dateLabel}</th>
              <th className="border-0" style={{whiteSpace: "nowrap"}}>Tasa del día</th>
              <th className="border-0 text-right">Monto</th>
              <th className="border-0 text-right">{shareLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td className="align-middle" style={{ maxWidth: "25%" }}>
                  <h6 className="mb-0">{orDash(row.name)}</h6>
                </td>
                <td className="align-middle" style={{ maxWidth: "25%" }}>
                  {orDash(row.description)}
                </td>
                <td className="align-middle text-nowrap">
                  {moment(row.expense_date).format("DD MMM YYYY")}
                </td>
                <td className="align-middle text-nowrap">
                  <NumberFormat
                    displayType="text"
                    prefix={`${quoteCurrencyIso} `}
                    allowedDecimalSeparators={true}
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator={true}
                    value={getRateForRow ? getRateForRow(row) : rateValue}
                  />
                </td>
                <td className="align-middle text-right text-nowrap">
                  {row.amount === null || row.amount === undefined ? (
                    "-"
                  ) : (
                    <Amount className="font-weight-semi-bold" value={row.amount} valueBs={row.amount_bs} customRate={getRateForRow ? getRateForRow(row) : null} />
                  )}
                </td>
                <td
                  className={classNames("align-middle text-right text-nowrap", {
                    "text-success": credit,
                  })}
                >
                  <Amount
                    className="font-weight-semi-bold"
                    value={signed(row[shareKey]) || 0}
                    valueBs={signed(row[`${shareKey}_bs`])}
                    customRate={getRateForRow ? getRateForRow(row) : null}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <th className="border-0 align-middle" colSpan={5}>
                {totalLabel}
              </th>
              <th
                className={classNames(
                  "border-0 text-right text-nowrap font-weight-semi-bold",
                  { "text-success": credit }
                )}
              >
                <Amount value={signed(total) || 0} valueBs={signed(totalBs)} isTotal />
              </th>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  };

  const exchangeRateValue = hasConversion ? (
    <>
      <NumberFormat
        displayType="text"
        prefix={`${baseCurrencySymbol} `}
        allowedDecimalSeparators={true}
        decimalScale={2}
        fixedDecimalScale
        thousandSeparator={true}
        value={1}
      />
      <NumberFormat
        displayType="text"
        prefix={` | ${quoteCurrencyIso} `}
        allowedDecimalSeparators={true}
        decimalScale={2}
        fixedDecimalScale
        thousandSeparator={true}
        value={rateValue}
      />
      {rateDateLabel ? ` (${rateDateLabel})` : ""}
    </>
  ) : (
    "No disponible"
  );

  return (
    <>
      {/* Cabecera, datos del inmueble y datos adicionales: el mismo componente
      que usa la pantalla del efecto por cobrar. */}
      <ItemImageTitleSubProperty
        image={logo || condominium?.logo}
        title1={"Planilla de liquidación de gastos"}
        titleCondominium={`Condominio: ${condominium?.name || ""}`}
        titleProperty={"Datos del inmueble"}
        titleInvoice={"Datos adicionales"}
        condominium={[
          {
            label: "Documento de identificación",
            value: condominium?.identification || "No disponible",
          },
          { label: "Dirección", value: condominium?.address || "No disponible" },
        ]}
        property={[
          { label: "Propietario", value: owner?.name || "No disponible" },
          { label: "Teléfono", value: owner?.phone || "No disponible" },
          { label: "Inmueble", value: owner?.property || "No disponible" },
          { label: "Alícuota", value: owner?.aliquot || "No disponible" },
        ]}
        invoice={[
          ...(dates?.control_number ? [{ label: "N° de control", value: dates.control_number }] : []),
          { label: "Fecha de emisión", value: formatDate(dates?.issued_at) },
          { label: "Fecha de vencimiento", value: formatDate(dates?.due_at) },
          { label: "Período", value: dates?.period_name || "No disponible" },
          { label: "Tasa de cambio", value: exchangeRateValue },
        ]}
        loading={false}
      />

      {/* Montos del período. Todos los bloques llevan la misma separación
      (mb-3) para que el reporte tenga un ritmo parejo: antes unos quedaban
      pegados y otros muy separados. */}
      <div className="border border-200 rounded mb-3">
        <Row className="g-0 text-center text-sm-left">
          <Col xs={12} sm={4} className="pl-4 pr-3 py-1">
            <div className="fs--1 text-600 font-weight-semi-bold mb-0">
              Monto total
            </div>
            <Amount
              value={amounts?.total}
              valueBs={amounts?.total_bs}
              className="fs-0 font-weight-bold text-900"
              isTotal
            />
          </Col>
          <Col xs={12} sm={4} className="pl-3 pr-4 py-1 border-left border-200">
            <div className="fs--1 text-600 font-weight-semi-bold mb-0">
              Monto abonado
            </div>
            <Amount
              value={amounts?.paid}
              valueBs={amounts?.paid_bs}
              className="fs-0 font-weight-bold text-900"
              isTotal
            />
          </Col>
          <Col xs={12} sm={4} className="pl-3 pr-4 py-1 border-left border-200">
            <div className="d-flex justify-content-between align-items-center mb-0">
              <span className="fs--1 text-600 font-weight-semi-bold">
                Saldo pendiente
              </span>
              <Badge
                className={classNames("rounded-capsule", {
                  "badge-soft-success": amounts?.is_fully_paid,
                  "badge-soft-warning": !amounts?.is_fully_paid,
                })}
              >
                {amounts?.is_fully_paid ? "Pagado" : "Pendiente"}
              </Badge>
            </div>
            <Amount
              value={amounts?.pending}
              valueBs={amounts?.pending_bs}
              className={classNames(
                "fs-0 font-weight-bold",
                amounts?.is_fully_paid ? "text-900" : "text-warning"
              )}
              isTotal
            />
          </Col>
        </Row>
      </div>

      {/* Deuda de todos los períodos, no solo la del que se está mirando. Va
      destacada en rojo suave, igual que en el PDF. */}
      <PlanillaBalanceTiles
        amounts={amounts}
        renderAmount={(value, valueBs) => (
          <Amount
            value={value}
            valueBs={valueBs}
            className="fs-0 font-weight-bold text-900"
            isTotal
          />
        )}
      />

      {/* Datos de pago */}
      {banks && banks.length > 0 && (
        <div className="fs--1">
          <Card
            style={{ border: "1px solid rgba(0, 0, 0, .1)" }}
            className="mb-3 shadow-none"
          >
            <CardTitle className="bg-light text-dark text-center fs-0 font-weight-bold py-2 m-0">
              Datos de pago
            </CardTitle>
            <Table responsive className="fs--1">
              <thead>
                <tr>
                  <th className="border-0">DNI</th>
                  <th className="border-0">Banco</th>
                  <th className="border-0">Titular</th>
                  <th className="border-0">Moneda</th>
                  <th className="border-0">Número de cuenta</th>
                </tr>
              </thead>
              <tbody>
                {banks.map((bank, index) => (
                  <tr key={index}>
                    <td className="align-middle">{orDash(bank.dni)}</td>
                    <td className="align-middle">{orDash(bank.bank)}</td>
                    <td className="align-middle">{orDash(bank.owner)}</td>
                    <td className="align-middle">{orDash(bank.currency)}</td>
                    <td className="align-middle">
                      {orDash(bank.account_number)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>
      )}

      {/* Las cinco tablas, en el mismo orden que el PDF */}
      <ExpenseTable
        title="Gastos ordinarios"
        rows={tables?.ordinary}
        totalLabel="Total de gastos ordinarios"
        total={totals?.ordinary}
        totalBs={totals?.ordinary_bs}
      />
      <ExpenseTable
        title="Gastos extraordinarios"
        rows={tables?.extraordinary}
        totalLabel="Total de gastos extraordinarios"
        total={totals?.extraordinary}
        totalBs={totals?.extraordinary_bs}
      />
      <ExpenseTable
        title="Gastos particulares"
        rows={tables?.particular}
        totalLabel="Total de gastos particulares"
        total={totals?.particular}
        totalBs={totals?.particular_bs}
      />
      <ExpenseTable
        title="Fondos de reserva"
        rows={tables?.funds}
        totalLabel="Total fondo de reserva"
        total={totals?.funds}
        totalBs={totals?.funds_bs}
      />
      <ExpenseTable
        title="Ingresos aplicados"
        rows={tables?.credits}
        totalLabel="Total ingresos descontados"
        total={totals?.credits}
        totalBs={totals?.credits_bs}
        shareKey="discount"
        shareLabel="Descuento"
        dateLabel="Fecha del ingreso"
        credit
      />

      {/* Total del período */}
      <div className="table-responsive mb-3 fs--1">
        <Table striped className="border-bottom">
          <thead>
            <tr>
              <th className="border-0 align-top">
                Total a pagar propietario
                {owner?.name ? ` (${owner.name})` : ""}
                {/* HO-TASK-254: el total va a la tasa del día elegido arriba,
                    no a la de cada gasto, y se dice. */}
                {hasConversion && showTotalsInQuote && (
                  <span className="d-block text-600 fs--1 font-weight-normal mt-1">
                    El monto total a pagar en bolívares es calculado en base a la tasa de cambio del día{" "}
                    {appliedRateDateLabel || rateDateLabel}
                  </span>
                )}
              </th>
              <th className="border-0 align-top text-right font-weight-semi-bold text-nowrap">
                <Amount value={amounts?.total} valueBs={amounts?.total_bs} isTotal />
              </th>
            </tr>
          </thead>
        </Table>

        {/* Pie con el logo de Homelan y la nota del condominio, igual que la
        vista del condominio completo. */}
        <div className="text-start text-muted mt-3 pt-1 mb-0">
          <Logo to={null} styleImage={{ width: "100px" }} className={"mb-2"} />
          {condominium?.note && (
            <div className="mb-0">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" size="sm" />
              <pre
                style={{
                  fontFamily: "Open Sans",
                  fontSize: ".83333rem",
                  color: "#5e6e82",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {condominium.note}
              </pre>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

PropertyPlanillaReport.propTypes = {
  planilla: PropTypes.object.isRequired,
  baseCurrencySymbol: PropTypes.string,
  quoteCurrencyIso: PropTypes.string,
  rateValue: PropTypes.number,
  rateDateLabel: PropTypes.string,
  appliedRateDateLabel: PropTypes.string,
  showItemsInQuote: PropTypes.bool,
  showTotalsInQuote: PropTypes.bool,
  logo: PropTypes.string,
};


export default PropertyPlanillaReport;
